import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { useMinistry } from '@/contexts/MinistryContext';
import { Users, UserCheck, UserPlus, Cake, TrendingUp, AlertTriangle, ThermometerSun, UserX, Phone, RefreshCw, GraduationCap, X, Download, FileSpreadsheet, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const Dashboard = () => {
  const { currentMinistry, loading: ministryLoading } = useMinistry();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [engagementAlerts, setEngagementAlerts] = useState(null);
  const [updatingEngagement, setUpdatingEngagement] = useState(false);
  const [turning18DialogOpen, setTurning18DialogOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState('all'); // 'all' ou ano específico

  useEffect(() => {
    if (!ministryLoading && currentMinistry?.id) {
      fetchDashboard();
      fetchEngagementAlerts();
    } else if (!ministryLoading && !currentMinistry) {
      // Sem ministério, para de carregar
      setLoading(false);
    }
  }, [currentMinistry?.id, ministryLoading, selectedYear]);

  const fetchDashboard = async () => {
    if (!currentMinistry?.id) return;
    setLoading(true);
    try {
      let url = `${API}/reports/dashboard?ministry_id=${currentMinistry.id}`;
      if (selectedYear && selectedYear !== 'all') {
        url += `&year=${selectedYear}`;
      }
      const response = await axios.get(url);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEngagementAlerts = async () => {
    if (!currentMinistry?.id) return;
    try {
      const response = await axios.get(`${API}/reports/engagement/alerts?ministry_id=${currentMinistry.id}`);
      setEngagementAlerts(response.data);
    } catch (error) {
      console.error('Error fetching engagement alerts:', error);
    }
  };

  const handleUpdateEngagement = async () => {
    setUpdatingEngagement(true);
    try {
      await axios.post(`${API}/reports/update-engagement?ministry_id=${currentMinistry?.id}`);
      toast.success('Status de engajamento atualizado!');
      fetchDashboard();
      fetchEngagementAlerts();
    } catch (error) {
      toast.error('Erro ao atualizar engajamento');
    } finally {
      setUpdatingEngagement(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Nunca';
    try {
      return format(parseISO(dateStr), "dd/MM/yyyy", { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  // Exportar lista de quem completa 18 anos para CSV
  const exportTurning18ToCSV = () => {
    if (!stats?.turning_18_soon || stats.turning_18_soon.length === 0) {
      toast.error('Nenhum adolescente para exportar');
      return;
    }

    const headers = ['Nome', 'Data 18 Anos', 'Dias Restantes', 'Telefone', 'Gênero', 'Status'];
    const rows = stats.turning_18_soon.map(a => [
      a.name || '',
      a.eighteenth_birthday ? format(parseISO(a.eighteenth_birthday), "dd/MM/yyyy", { locale: ptBR }) : '',
      a.days_until_18 || 0,
      a.phone || '',
      a.gender === 'masculino' ? 'Masculino' : 'Feminino',
      a.status === 'membro' ? 'Membro' : 'Visitante'
    ]);

    const BOM = '\uFEFF';
    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(';'))
    ].join('\n');

    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `completando_18_anos_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(`${stats.turning_18_soon.length} adolescente(s) exportado(s)`);
  };

  // Exportar lista de quem completa 18 anos para Excel
  const exportTurning18ToExcel = () => {
    if (!stats?.turning_18_soon || stats.turning_18_soon.length === 0) {
      toast.error('Nenhum adolescente para exportar');
      return;
    }

    let tableHTML = `
      <table>
        <thead>
          <tr>
            <th>Nome</th>
            <th>Data 18 Anos</th>
            <th>Dias Restantes</th>
            <th>Telefone</th>
            <th>Gênero</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
    `;

    stats.turning_18_soon.forEach(a => {
      const birthday18 = a.eighteenth_birthday ? format(parseISO(a.eighteenth_birthday), "dd/MM/yyyy", { locale: ptBR }) : '';
      tableHTML += `
        <tr>
          <td>${a.name || ''}</td>
          <td>${birthday18}</td>
          <td>${a.days_until_18 || 0}</td>
          <td>${a.phone || ''}</td>
          <td>${a.gender === 'masculino' ? 'Masculino' : 'Feminino'}</td>
          <td>${a.status === 'membro' ? 'Membro' : 'Visitante'}</td>
        </tr>
      `;
    });

    tableHTML += '</tbody></table>';

    const blob = new Blob([tableHTML], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `completando_18_anos_${new Date().toISOString().split('T')[0]}.xls`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(`${stats.turning_18_soon.length} adolescente(s) exportado(s)`);
  };

  if (loading) {
    return <div className="text-center py-12">Carregando...</div>;
  }

  return (
    <div className="fade-in" data-testid="dashboard-page">
      <div className="mb-6 lg:mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-4xl font-bold text-gray-900 mb-1 lg:mb-2" style={{fontFamily: 'Space Grotesk'}}>Dashboard</h1>
          <p className="text-sm lg:text-lg text-gray-600">Visão geral do ministério</p>
        </div>
        
        {/* Filtro de Ano */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border shadow-sm">
            <Calendar size={18} className="text-gray-500" />
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="w-[140px] border-0 shadow-none focus:ring-0" data-testid="year-filter">
                <SelectValue placeholder="Filtrar por ano" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">📊 Todos os anos</SelectItem>
                {stats?.available_years?.map(year => (
                  <SelectItem key={year} value={year}>📅 {year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {selectedYear !== 'all' && (
            <span className="text-sm text-purple-600 font-medium bg-purple-50 px-3 py-1 rounded-full">
              Filtrando: {selectedYear}
            </span>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-6 gap-3 lg:gap-6 mb-6 lg:mb-8">
        <div className="stat-card" data-testid="stat-total-adolescents">
          <div className="stat-icon">
            <Users size={28} />
          </div>
          <div className="stat-value">{stats?.total_adolescents || 0}</div>
          <div className="stat-label">Total de Adolescentes</div>
          {stats?.engagement && (
            <div className="flex justify-between mt-3 pt-3 border-t border-gray-100 text-xs">
              <span className="flex items-center text-green-600">
                <span className="w-2 h-2 rounded-full bg-green-500 mr-1"></span>
                {stats.engagement.active}
              </span>
              <span className="flex items-center text-amber-600">
                <span className="w-2 h-2 rounded-full bg-amber-500 mr-1"></span>
                {stats.engagement.disengaged}
              </span>
              <span className="flex items-center text-red-600">
                <span className="w-2 h-2 rounded-full bg-red-500 mr-1"></span>
                {stats.engagement.inactive}
              </span>
            </div>
          )}
        </div>

        <div className="stat-card" data-testid="stat-present-today">
          <div className="stat-icon" style={{background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)', color: '#065f46'}}>
            <UserCheck size={28} />
          </div>
          <div className="stat-value">{stats?.present_today || 0}</div>
          <div className="stat-label">Presentes Hoje</div>
        </div>

        <div className="stat-card" data-testid="stat-average-attendance">
          <div className="stat-icon" style={{background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)', color: '#1e40af'}}>
            <TrendingUp size={28} />
          </div>
          <div className="stat-value">{stats?.average_attendance || 0}</div>
          <div className="stat-label">Média de Presença</div>
        </div>

        <div className="stat-card" data-testid="stat-first-time-visitors">
          <div className="stat-icon" style={{background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)', color: '#3730a3'}}>
            <UserPlus size={28} />
          </div>
          <div className="stat-value">{stats?.first_time_visitors || 0}</div>
          <div className="stat-label">Visitantes de Primeira Vez</div>
        </div>

        <div className="stat-card" data-testid="stat-birthdays">
          <div className="stat-icon" style={{background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', color: '#92400e'}}>
            <Cake size={28} />
          </div>
          <div className="stat-value">{stats?.birthdays_this_week?.length || 0}</div>
          <div className="stat-label">Aniversários esta Semana</div>
        </div>

        {/* Turning 18 Soon - Ministry Transition */}
        <button 
          onClick={() => setTurning18DialogOpen(true)}
          className="stat-card text-left hover:shadow-lg transition-all cursor-pointer" 
          data-testid="stat-turning-18"
        >
          <div className="stat-icon" style={{background: 'linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%)', color: '#9d174d'}}>
            <GraduationCap size={28} />
          </div>
          <div className="stat-value">{stats?.turning_18_soon?.length || 0}</div>
          <div className="stat-label">Completam 18 anos em 2 meses</div>
          {stats?.turning_18_soon?.length > 0 && (
            <div className="text-xs text-pink-600 mt-2">Clique para ver lista</div>
          )}
        </button>
      </div>

      {/* Engagement Thermometer */}
      {stats?.engagement && (
        <div className="card mb-8 border-l-4 border-l-orange-500">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <ThermometerSun className="text-orange-500" size={28} />
              <h2 className="text-xl font-bold text-gray-900" style={{fontFamily: 'Space Grotesk'}}>
                Termômetro de Engajamento
              </h2>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleUpdateEngagement}
              disabled={updatingEngagement}
              className="flex items-center space-x-2"
            >
              <RefreshCw size={16} className={updatingEngagement ? 'animate-spin' : ''} />
              <span>Atualizar</span>
            </Button>
          </div>
          
          {/* Visual Thermometer Bar */}
          <div className="mb-6">
            <div className="flex rounded-full overflow-hidden h-8">
              <div 
                className="bg-gradient-to-r from-green-400 to-green-500 flex items-center justify-center text-white text-sm font-bold transition-all"
                style={{ width: `${(stats.engagement.active / (stats.engagement.active + stats.engagement.disengaged + stats.engagement.inactive)) * 100}%` }}
              >
                {stats.engagement.active > 0 && stats.engagement.active}
              </div>
              <div 
                className="bg-gradient-to-r from-amber-400 to-amber-500 flex items-center justify-center text-white text-sm font-bold transition-all"
                style={{ width: `${(stats.engagement.disengaged / (stats.engagement.active + stats.engagement.disengaged + stats.engagement.inactive)) * 100}%` }}
              >
                {stats.engagement.disengaged > 0 && stats.engagement.disengaged}
              </div>
              <div 
                className="bg-gradient-to-r from-red-400 to-red-500 flex items-center justify-center text-white text-sm font-bold transition-all"
                style={{ width: `${(stats.engagement.inactive / (stats.engagement.active + stats.engagement.disengaged + stats.engagement.inactive)) * 100}%` }}
              >
                {stats.engagement.inactive > 0 && stats.engagement.inactive}
              </div>
            </div>
            <div className="flex justify-between mt-2 text-xs lg:text-sm flex-wrap gap-2">
              <span className="flex items-center text-green-600">
                <span className="w-2 h-2 lg:w-3 lg:h-3 rounded-full bg-green-500 mr-1 lg:mr-2"></span>
                Ativos ({stats.engagement.active})
              </span>
              <span className="flex items-center text-amber-600">
                <span className="w-2 h-2 lg:w-3 lg:h-3 rounded-full bg-amber-500 mr-1 lg:mr-2"></span>
                Desengajados ({stats.engagement.disengaged})
              </span>
              <span className="flex items-center text-red-600">
                <span className="w-2 h-2 lg:w-3 lg:h-3 rounded-full bg-red-500 mr-1 lg:mr-2"></span>
                Inativos ({stats.engagement.inactive})
              </span>
            </div>
          </div>

          {/* Alert Cards */}
          {(stats.engagement.disengaged > 0 || stats.engagement.inactive > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {stats.engagement.disengaged > 0 && (
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex items-center space-x-2 mb-2">
                    <AlertTriangle className="text-amber-600" size={20} />
                    <h3 className="font-bold text-amber-800">Atenção: Desengajados</h3>
                  </div>
                  <p className="text-sm text-amber-700 mb-3">
                    {stats.engagement.disengaged} adolescente(s) não participam há mais de 3 semanas
                  </p>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {stats.engagement.disengaged_list?.map((a, i) => (
                      <div key={i} className="text-sm text-amber-800 flex justify-between">
                        <span>{a.name}</span>
                        <span className="text-amber-600">{a.total_checkins} presenças</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {stats.engagement.inactive > 0 && (
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <div className="flex items-center space-x-2 mb-2">
                    <UserX className="text-red-600" size={20} />
                    <h3 className="font-bold text-red-800">Crítico: Inativos</h3>
                  </div>
                  <p className="text-sm text-red-700 mb-3">
                    {stats.engagement.inactive} adolescente(s) não participam há mais de 2 meses
                  </p>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {stats.engagement.inactive_list?.map((a, i) => (
                      <div key={i} className="text-sm text-red-800 flex justify-between">
                        <span>{a.name}</span>
                        <span className="text-red-600">{a.total_checkins} presenças</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* PG Stats Card */}
      {stats?.pg_stats && (
        <div className="card mb-8 border-l-4 border-l-purple-500">
          <div className="flex items-center space-x-3 mb-4">
            <Users className="text-purple-500" size={28} />
            <h2 className="text-xl font-bold text-gray-900" style={{fontFamily: 'Space Grotesk'}}>
              Pequenos Grupos (Adolescentes Ativos)
            </h2>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-purple-700">{stats.pg_stats.active_in_pg}</p>
              <p className="text-sm text-purple-600">Em Pequenos Grupos</p>
            </div>
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-orange-700">{stats.pg_stats.active_without_pg}</p>
              <p className="text-sm text-orange-600">Sem Pequeno Grupo</p>
            </div>
          </div>
          
          {stats.pg_stats.active_without_pg > 0 && (
            <p className="text-sm text-gray-600 mt-3">
              ⚠️ {stats.pg_stats.active_without_pg} adolescentes ativos ainda não estão em nenhum Pequeno Grupo
            </p>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Check-ins */}
        <div className="card">
          <h2 className="text-xl font-bold text-gray-900 mb-4" style={{fontFamily: 'Space Grotesk'}}>Check-ins Recentes</h2>
          {stats?.recent_checkins && stats.recent_checkins.length > 0 ? (
            <div className="space-y-3">
              {stats.recent_checkins.map((checkin) => (
                <div key={checkin.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg" data-testid="recent-checkin-item">
                  <div>
                    <p className="font-semibold text-gray-900">{checkin.adolescent_name}</p>
                    <p className="text-sm text-gray-500">{checkin.event_name}</p>
                  </div>
                  <div className="text-right">
                    <span className={`badge badge-${checkin.status}`}>{checkin.status === 'checked_in' ? 'Presente' : 'Saiu'}</span>
                    <p className="text-xs text-gray-500 mt-1">
                      {format(parseISO(checkin.checkin_time), 'HH:mm', { locale: ptBR })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">Nenhum check-in recente</p>
          )}
        </div>

        {/* Birthdays This Week */}
        <div className="card">
          <h2 className="text-xl font-bold text-gray-900 mb-4" style={{fontFamily: 'Space Grotesk'}}>Aniversariantes da Semana</h2>
          {stats?.birthdays_this_week && stats.birthdays_this_week.length > 0 ? (
            <div className="space-y-3">
              {stats.birthdays_this_week.map((birthday, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gradient-to-r from-yellow-50 to-pink-50 rounded-lg" data-testid="birthday-item">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full bg-yellow-200 flex items-center justify-center">
                      <Cake size={20} className="text-yellow-700" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{birthday.name}</p>
                      <p className="text-sm text-gray-500">
                        {format(parseISO(birthday.date), "dd 'de' MMMM", { locale: ptBR })}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-yellow-700">
                      {birthday.days_until === 0 ? 'Hoje!' : `Em ${birthday.days_until} dia${birthday.days_until > 1 ? 's' : ''}`}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">Nenhum aniversário esta semana</p>
          )}
        </div>
      </div>

      {/* Full Engagement Alerts Section */}
      {engagementAlerts && (engagementAlerts.disengaged_count > 0 || engagementAlerts.inactive_count > 0) && (
        <div className="mt-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4" style={{fontFamily: 'Space Grotesk'}}>
            Adolescentes que Precisam de Atenção
          </h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Disengaged Full List */}
            {engagementAlerts.disengaged_count > 0 && (
              <div className="card border-l-4 border-l-amber-500">
                <h3 className="font-bold text-amber-800 mb-4 flex items-center">
                  <AlertTriangle className="mr-2" size={20} />
                  Desengajados ({engagementAlerts.disengaged_count})
                </h3>
                <p className="text-sm text-gray-600 mb-4">+3 semanas sem participar ou taxa de presença &lt;50%</p>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {engagementAlerts.disengaged.map((a) => (
                    <div key={a.id} className="p-3 bg-amber-50 rounded-lg">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold text-gray-900">{a.name}</p>
                          <p className="text-sm text-gray-500">Último check-in: {formatDate(a.last_checkin_date)}</p>
                          <div className="flex space-x-3 mt-1">
                            {a.days_since_last_checkin && (
                              <span className="text-xs text-amber-600 font-medium">
                                {a.days_since_last_checkin} dias ausente
                              </span>
                            )}
                            {a.attendance_rate !== undefined && (
                              <span className={`text-xs font-medium ${a.attendance_rate < 50 ? 'text-red-600' : 'text-green-600'}`}>
                                {a.attendance_rate}% presença
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          {a.phone && (
                            <a href={`tel:${a.phone}`} className="text-blue-600 hover:text-blue-800 flex items-center text-sm">
                              <Phone size={14} className="mr-1" />
                              {a.phone}
                            </a>
                          )}
                          <p className="text-xs text-gray-500 mt-1">{a.checkins_this_year || 0}/{a.total_checkins} presenças</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Inactive Full List */}
            {engagementAlerts.inactive_count > 0 && (
              <div className="card border-l-4 border-l-red-500">
                <h3 className="font-bold text-red-800 mb-4 flex items-center">
                  <UserX className="mr-2" size={20} />
                  Inativos ({engagementAlerts.inactive_count})
                </h3>
                <p className="text-sm text-gray-600 mb-4">Mais de 2 meses sem participar</p>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {engagementAlerts.inactive.map((a) => (
                    <div key={a.id} className="p-3 bg-red-50 rounded-lg">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold text-gray-900">{a.name}</p>
                          <p className="text-sm text-gray-500">Último check-in: {formatDate(a.last_checkin_date)}</p>
                          {a.days_since_last_checkin && (
                            <p className="text-xs text-red-600 font-medium">
                              {a.days_since_last_checkin} dias sem participar
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          {a.phone && (
                            <a href={`tel:${a.phone}`} className="text-blue-600 hover:text-blue-800 flex items-center text-sm">
                              <Phone size={14} className="mr-1" />
                              {a.phone}
                            </a>
                          )}
                          <p className="text-xs text-gray-500 mt-1">{a.total_checkins} presenças total</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Turning 18 Dialog */}
      <Dialog open={turning18DialogOpen} onOpenChange={setTurning18DialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <GraduationCap className="text-pink-600" size={24} />
              <span>Adolescentes Completando 18 Anos (Próximos 2 Meses)</span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="mt-4">
            <div className="bg-pink-50 border border-pink-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-pink-800">
                <strong>⚠️ Atenção:</strong> Estes adolescentes precisam ser preparados para a transição para outro ministério. 
                Duas semanas após completarem 18 anos, o status será alterado automaticamente para <strong>Inativo</strong>.
              </p>
            </div>

            {/* Botões de Exportação */}
            {stats?.turning_18_soon && stats.turning_18_soon.length > 0 && (
              <div className="flex gap-2 mb-4">
                <Button variant="outline" size="sm" onClick={exportTurning18ToCSV} data-testid="export-18-csv">
                  <FileSpreadsheet size={16} className="mr-1 text-green-600" />
                  Exportar CSV
                </Button>
                <Button variant="outline" size="sm" onClick={exportTurning18ToExcel} data-testid="export-18-excel">
                  <FileSpreadsheet size={16} className="mr-1 text-blue-600" />
                  Exportar Excel
                </Button>
                <span className="text-sm text-gray-500 ml-auto flex items-center">
                  {stats.turning_18_soon.length} adolescente(s)
                </span>
              </div>
            )}

            {stats?.turning_18_soon && stats.turning_18_soon.length > 0 ? (
              <div className="space-y-3">
                {stats.turning_18_soon.map((adolescent) => (
                  <div 
                    key={adolescent.id} 
                    className="flex items-center justify-between p-4 bg-gradient-to-r from-pink-50 to-purple-50 rounded-lg border border-pink-100"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 rounded-full bg-pink-200 flex items-center justify-center">
                        <GraduationCap size={24} className="text-pink-700" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{adolescent.name}</p>
                        <p className="text-sm text-gray-500">
                          Faz 18 em: {format(parseISO(adolescent.eighteenth_birthday), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-bold ${adolescent.days_until_18 <= 14 ? 'text-red-600' : adolescent.days_until_18 <= 30 ? 'text-orange-600' : 'text-pink-600'}`}>
                        {adolescent.days_until_18 === 0 ? 'Hoje!' : `Em ${adolescent.days_until_18} dias`}
                      </p>
                      {adolescent.phone && (
                        <a href={`tel:${adolescent.phone}`} className="text-blue-600 hover:text-blue-800 flex items-center text-xs mt-1 justify-end">
                          <Phone size={12} className="mr-1" />
                          {adolescent.phone}
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">
                Nenhum adolescente completa 18 anos nos próximos 2 meses
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;